import * as React from 'react';
import {useDispatch} from 'react-redux';

import {Routing} from 'nutshell-core/routing';
import {removeSavedFilter} from 'nutshell-core/saved-filters/saved-filters-actions';
import type {CoreEntityType} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {SAVED_LIST_TOUR_SAVE_BUTTON_ID} from 'shells/tours/tours';

import type {Flow} from 'flow-to-typescript-codemod';

import {appHistory} from '../../../../history';
import {addNotification} from '../../../../notifications/index';

import {useDeleteSavedFilter, useDeleteAudienceForEntityList} from '../../graphql/hooks';

import {getDefaultListLink} from '../helpers';
import type {SidebarSectionType} from '../types';

import {NavItem} from './nav-item';
import type {Props as NavItemProps} from './nav-item';

type NavItemPropsNotNeeded = {
    onDelete?: () => void;
    isTwoLine: boolean;
};

type NavItemPropsNeeded = Flow.Diff<NavItemProps, NavItemPropsNotNeeded>;

type Props = NavItemPropsNeeded & {
    type: SidebarSectionType;
    entityType: CoreEntityType;
    searchNameQuery: string | null | undefined;
};

export function NavItemWrapper(props: Props) {
    const {searchNameQuery, entityType, type, ...restProps} = props;

    const {deleteSavedFilter} = useDeleteSavedFilter(entityType, searchNameQuery);
    const {deleteAudienceForEntityList} = useDeleteAudienceForEntityList(searchNameQuery);
    const dispatch = useDispatch();

    const deleteFilterOnClickConfirmationMessage = () => {
        // @ts-expect-error
        deleteSavedFilter({id: restProps.navItem.id})
            .then(() => {
                // @ts-expect-error
                dispatch(removeSavedFilter(restProps.navItem.id));

                if (restProps.navItem.isActive) {
                    const view = Routing.deparam(window.location.search).view;
                    const redirect = getDefaultListLink(entityType, view);

                    appHistory.replace(redirect);
                }
            })
            .catch(() => {
                addNotification('Whoops!', {
                    body: 'Something went wrong. Please try again later.',
                    icon: 'frowny',
                });
            });
    };

    const deleteAudienceOnClickConfirmationMessage = () => {
        // Redirect and close confirmation dialog before running deletAudienceForEntityList mutation
        // The mutation does not just set the deleted_time column in em_audiences
        // It also deletes all audience members from the em_audience_members table which can take a while for large audiences
        // Timeout has no functional purpose, just smoother UI if dialog does not close immediately
        setTimeout(() => {
            if (restProps.navItem.isActive) {
                const view = Routing.deparam(window.location.search).view;
                const redirect = getDefaultListLink(props.entityType, view);

                appHistory.replace(redirect);
            }
        }, 100);

        // @ts-expect-error
        deleteAudienceForEntityList({id: restProps.navItem.id}).catch(() => {
            addNotification('Whoops!', {
                body: 'Something went wrong. Please try again later.',
                icon: 'frowny',
            });
        });
    };

    let handleDelete;
    if (restProps.navItem.isDeleteable) {
        handleDelete =
            restProps.navItem.type === 'list'
                ? deleteFilterOnClickConfirmationMessage
                : deleteAudienceOnClickConfirmationMessage;
    }

    return (
        <div className='flex flex-dir-col'>
            <NavItem
                {...restProps}
                onDelete={handleDelete}
                isTwoLine={
                    (type === 'shared' || type === 'saved') && NutClientConfig.hasSharedListsFF
                }
            />
            {/* Default filters are immutable so we should never show the save button */}
            {restProps.navItem.isDirty && (
                <div className='align-self-end width-fit-content my-4'>
                    <Button
                        id={SAVED_LIST_TOUR_SAVE_BUTTON_ID}
                        variant='primary'
                        size='mini'
                        onClick={restProps.onEdit}
                    >
                        {getSaveButtonText(type)}
                    </Button>
                </div>
            )}
        </div>
    );
}

function getSaveButtonText(type: SidebarSectionType): string {
    switch (type) {
        case 'draft':
            return 'Save list';
        case 'shared':
            return 'Save as new';
        case 'saved':
        default:
            return 'Save changes';
    }
}
